import React, { useCallback, useEffect, useRef, useState } from 'react';
import Add from '@mui/icons-material/Add';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import CardGiftcardRounded from '@mui/icons-material/CardGiftcardRounded';
import Remove from '@mui/icons-material/Remove';
import ShoppingBagOutlined from '@mui/icons-material/ShoppingBagOutlined';
import {
  Box,
  CardMedia,
  CircularProgress,
  Divider,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { trackV2InitiateCheckout } from '../services/tracking';
import { useV2Session } from '../state/SessionProvider';
import { paths } from '../config/paths';
import { getProductUrlSlug } from '../utils/productUrl';

import noImage from '../assets/no-image.png';
import Button from '../components/Button';
import FreeGiftBadge from '../components/FreeGiftBadge';
import CartPageHeader from '../components/cart/CartPageHeader';
import CartSuggestions from '../components/cart/CartSuggestions';
import LoyaltyOtpField from '../components/cart/LoyaltyOtpField';
import FreeDeliveryPromotion from '../components/cart/FreeDeliveryPromotion';
import ProductCustomizationSheet from '../components/product/ProductCustomizationSheet';
import { selectionFromStoredCustomizations } from '../components/product/productCustomizationHelpers';
import { useCustomizationSelection } from '../components/product/useCustomizationSelection';
import PageContainer from '../layouts/PageContainer';
import { storageGet } from '../platform/storage';
import { v2Colors } from '../design/theme';
import {
  addToCartItem,
  clearCart,
  computeCartCustomizationTotal,
  computeLineUnitPrice,
  formatCartCustomizationSummary,
  getAppliedLoyaltyDiscountRupee,
  getCart,
  getCartLineKey,
  getProductById,
  isLoyaltyOtpComplete,
  listCartCustomizationAddOns,
  normalizeLoyaltyOtp,
  setCartInstructions,
  setCartLineQuantity,
  setCartLoyalty,
  setCartLoyaltyOtp,
} from '../services/commerce';
import { initiateLoyaltyRedemption } from '../services/cart';
import { V2ApiError } from '../services/client';

const LOCATION_KEY = 'v2_location';

const cardSx = {
  bgcolor: v2Colors.white,
  borderRadius: 3,
  p: { xs: 1.75, sm: 2 },
  mb: 2,
  boxShadow: 'none',
};

function CartLineRow({ line, onDecrease, onIncrease, onOpenProduct, busy }) {
  const qty = Number(line.quantity) || 0;
  const unit = Number(line.price) || 0;
  const lineTotal = unit * qty;
  const addOns = listCartCustomizationAddOns(line.customizations);
  const tagLabels =
    addOns.length > 0
      ? addOns.map((a) => a.name)
      : formatCartCustomizationSummary(line.customizations)
        ? formatCartCustomizationSummary(line.customizations).split(' · ')
        : [];

  return (
    <Box sx={cardSx}>
      <Box sx={{ display: 'flex', gap: 1.5 }}>
        <CardMedia
          component="img"
          image={line.imageUrl || noImage}
          alt={line.name}
          sx={{
            width: 72,
            height: 72,
            flexShrink: 0,
            borderRadius: 2,
            objectFit: 'cover',
            bgcolor: v2Colors.surface,
          }}
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = noImage;
          }}
        />
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <Typography
            fontWeight={700}
            sx={{
              fontSize: '1.05rem',
              lineHeight: 1.25,
              color: v2Colors.heading,
              cursor: line.productId ? 'pointer' : 'default',
              mb: tagLabels.length ? 0.75 : 1,
            }}
            onClick={() => {
              const slug = line.urlId || getProductUrlSlug({ name: line.name });
              if (slug) onOpenProduct?.(slug);
            }}
          >
            {line.name}
            {line.isFreeSkuItem ? <FreeGiftBadge /> : null}
          </Typography>
          {tagLabels.length ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1 }}>
              {tagLabels.map((tag, tagIndex) => (
                <Box
                  key={`${line.productId}-${tag}-${tagIndex}`}
                  sx={{
                    px: 1,
                    py: 0.35,
                    borderRadius: 1,
                    bgcolor: v2Colors.tagFill,
                    color: v2Colors.tagText,
                    fontSize: '0.72rem',
                    fontWeight: 600,
                    lineHeight: 1.2,
                  }}
                >
                  {tag}
                </Box>
              ))}
            </Box>
          ) : null}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              mt: 'auto',
            }}
          >
            <Typography fontWeight={700} sx={{ fontSize: '1.1rem', color: v2Colors.heading }}>
              ₹{lineTotal}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                bgcolor: v2Colors.surface,
                borderRadius: 10,
                px: 0.5,
                py: 0.25,
                minHeight: 36,
              }}
            >
              <Box
                component="button"
                type="button"
                aria-label="Decrease quantity"
                disabled={busy || qty <= 0}
                onClick={() => onDecrease(line)}
                sx={{
                  border: 'none',
                  bgcolor: 'transparent',
                  color: v2Colors.primary,
                  width: 32,
                  height: 32,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: busy || qty <= 0 ? 'not-allowed' : 'pointer',
                  opacity: busy || qty <= 0 ? 0.4 : 1,
                  fontFamily: 'inherit',
                }}
              >
                <Remove fontSize="small" />
              </Box>
              <Typography fontWeight={700} sx={{ minWidth: 24, textAlign: 'center', color: v2Colors.primary }}>
                {qty}
              </Typography>
              <Box
                component="button"
                type="button"
                aria-label="Increase quantity"
                disabled={busy || line.isFreeSkuItem}
                onClick={() => onIncrease(line)}
                sx={{
                  border: 'none',
                  bgcolor: 'transparent',
                  color: v2Colors.primary,
                  width: 32,
                  height: 32,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: busy ? 'not-allowed' : 'pointer',
                  opacity: busy ? 0.4 : 1,
                  fontFamily: 'inherit',
                }}
              >
                <Add fontSize="small" />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

const pointsSwitchSx = {
  '& .MuiSwitch-switchBase.Mui-checked': { color: v2Colors.white },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: v2Colors.primary, opacity: 1 },
  '& .MuiSwitch-track': { bgcolor: v2Colors.borderMuted, opacity: 1 },
};

function NaatuPointsRow({
  points,
  selected,
  confirmed,
  discount,
  disabled,
  blockedByCoupon,
  onToggle,
  sentTo,
  otp,
  onOtpChange,
  onConfirm,
  onResend,
  resending,
  error,
}) {
  return (
    <Box
      sx={{
        bgcolor: v2Colors.selectedFill,
        borderRadius: 2,
        p: 1.5,
        my: 1.25,
        opacity: blockedByCoupon ? 0.7 : 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
        <Box
          aria-hidden
          sx={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            bgcolor: 'rgba(165, 42, 60, 0.12)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <CardGiftcardRounded sx={{ fontSize: 20, color: v2Colors.primary }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              color: v2Colors.primary,
              fontWeight: 800,
              fontSize: '0.85rem',
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              lineHeight: 1.3,
            }}
          >
            Naatu points
          </Typography>
          <Typography variant="body2" sx={{ color: v2Colors.heading, fontWeight: 600, lineHeight: 1.35 }}>
            Use {points} points on this order
          </Typography>
          <Typography variant="caption" sx={{ color: v2Colors.textMuted, display: 'block', lineHeight: 1.35, mt: 0.25 }}>
            {blockedByCoupon
              ? 'A coupon is applied. Remove it at checkout to use points.'
              : `Save ₹${points}. Points and coupons can’t be used together.`}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          <Typography variant="caption" sx={{ color: v2Colors.heading, mr: 0.25 }}>
            Use points
          </Typography>
          <Switch
            checked={selected}
            disabled={disabled}
            onChange={onToggle}
            inputProps={{ 'aria-label': 'Use Naatu points' }}
            sx={pointsSwitchSx}
          />
        </Box>
      </Box>
      {selected ? (
        <LoyaltyOtpField
          sentTo={sentTo}
          value={otp}
          onChange={onOtpChange}
          onConfirm={onConfirm}
          onResend={onResend}
          confirmed={confirmed}
          discount={discount}
          resending={resending}
          disabled={disabled}
        />
      ) : null}
      {error ? (
        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.75, lineHeight: 1.4 }}>
          {error}
        </Typography>
      ) : null}
    </Box>
  );
}

function SummaryRow({ label, value, bold }) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        gap: 2,
        mb: bold ? 0 : 1,
      }}
    >
      <Typography
        variant="body2"
        sx={{
          color: bold ? v2Colors.heading : v2Colors.textMuted,
          fontWeight: bold ? 700 : 400,
        }}
      >
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: bold ? 700 : 600, color: v2Colors.heading }}>
        {value}
      </Typography>
    </Box>
  );
}

export default function Cart() {
  const navigate = useNavigate();
  const { mobile, user } = useV2Session();
  const [cart, setCart] = useState({
    products: [],
    totalAmount: 0,
    totalCount: 0,
    loyalty: null,
    coupon: null,
  });
  const [loyaltyOffer, setLoyaltyOffer] = useState(undefined);
  const [loyaltyError, setLoyaltyError] = useState('');
  const [otpDraft, setOtpDraft] = useState('');
  const [resendingOtp, setResendingOtp] = useState(false);
  const otpTouchedRef = useRef(false);
  const otpDraftRef = useRef('');
  const [instructionInput, setInstructionInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // Customize-on-"+": reopen the sheet for a cart line so a different customization becomes a new line.
  const [customizeLine, setCustomizeLine] = useState(null);
  const [customizeProduct, setCustomizeProduct] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [addingCustom, setAddingCustom] = useState(false);
  const customization = useCustomizationSelection(customizeProduct);

  const loadCart = useCallback(async () => {
    const next = await getCart();
    const storedPoints = Math.floor(Number(next.loyalty?.points) || 0);
    const storedOtp = normalizeLoyaltyOtp(next.loyalty?.otp);
    setCart({
      products: Array.isArray(next.products) ? next.products : [],
      totalAmount: Number(next.totalAmount) || 0,
      totalCount: Number(next.totalCount) || 0,
      loyalty: storedPoints
        ? {
            points: storedPoints,
            otp: storedOtp,
            sentTo: next.loyalty?.sentTo ? String(next.loyalty.sentTo) : '',
          }
        : null,
      coupon: next.coupon?.couponCode ? next.coupon : null,
    });
    if (!otpTouchedRef.current) setOtpDraft(storedOtp);
    setInstructionInput(typeof next.instructions === 'string' ? next.instructions : '');
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        await loadCart();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadCart]);

  useEffect(() => {
    const onCartChanged = (event) => {
      if (event?.detail?.source === 'loyalty-otp') return;
      loadCart();
    };
    window.addEventListener('v2-cart-changed', onCartChanged);
    return () => window.removeEventListener('v2-cart-changed', onCartChanged);
  }, [loadCart]);

  const handleLoyaltyOffer = useCallback((offer) => {
    setLoyaltyOffer(offer ?? null);
  }, []);

  const hasItems = cart.products.length > 0 && cart.totalCount > 0;
  const offerPoints = Math.max(0, Math.floor(Number(loyaltyOffer?.points) || 0));
  const appliedPoints = Math.max(0, Math.floor(Number(cart.loyalty?.points) || 0));

  useEffect(() => {
    if (loyaltyOffer === undefined && hasItems) return;
    if (appliedPoints > 0 && (!hasItems || appliedPoints !== offerPoints)) {
      setCartLoyalty(null).catch(() => {});
    }
  }, [loyaltyOffer, offerPoints, appliedPoints, hasItems]);

  const setLineQty = async (lineKey, nextQty) => {
    if (!lineKey) return;
    setBusy(true);
    try {
      await setCartLineQuantity({ lineKey }, nextQty);
      await loadCart();
    } finally {
      setBusy(false);
    }
  };

  const handleDecrease = async (line) => {
    if (!line || busy) return;
    await setLineQty(getCartLineKey(line), (Number(line.quantity) || 0) - 1);
  };

  const handleIncrease = async (line) => {
    if (!line || busy || line.isFreeSkuItem) return;
    const qty = Number(line.quantity) || 0;
    const hasCustom = line.customizations && Object.keys(line.customizations).length > 0;
    if (!hasCustom) {
      await setLineQty(getCartLineKey(line), qty + 1);
      return;
    }
    // Fetch the product so the sheet can render options + apply skinless/Smoke & Turmeric rules.
    setBusy(true);
    try {
      const loc = await storageGet(LOCATION_KEY);
      const res = await getProductById({ id: line.urlId || line.productId, storeId: loc?.storeId ?? null });
      const p = res?.data ?? null;
      if (!p?.customizations || Object.keys(p.customizations).length === 0) {
        await setCartLineQuantity({ lineKey: getCartLineKey(line) }, qty + 1);
        await loadCart();
        return;
      }
      setCustomizeProduct(p);
      setCustomizeLine(line);
      customization.setSelected(selectionFromStoredCustomizations(p, line.customizations));
      setSheetOpen(true);
    } finally {
      setBusy(false);
    }
  };

  const closeCustomizeSheet = () => {
    setSheetOpen(false);
    setCustomizeLine(null);
    setCustomizeProduct(null);
  };

  const handleConfirmCustomize = async () => {
    if (!customizeLine || !customizeProduct || addingCustom) return;
    setAddingCustom(true);
    try {
      await addToCartItem({
        productId: customizeLine.productId,
        urlId: customizeLine.urlId,
        name: customizeLine.name,
        imageUrl: customizeLine.imageUrl,
        variant: customizeLine.variant,
        customizations: customization.resolved,
      });
      closeCustomizeSheet();
      await loadCart();
    } finally {
      setAddingCustom(false);
    }
  };

  const handleClearAll = async () => {
    if (!hasItems || busy) return;
    if (!window.confirm('Remove all items from your bag?')) return;
    setBusy(true);
    try {
      await clearCart();
      await loadCart();
    } finally {
      setBusy(false);
    }
  };

  const goToProduct = (productSlug) => {
    if (productSlug) navigate(paths.product(productSlug));
  };

  const total = Number(cart.totalAmount) || 0;
  const customizationTotal = computeCartCustomizationTotal(cart.products);
  const itemSubtotal = Math.max(0, total - customizationTotal);
  const couponApplied = Boolean(cart.coupon?.couponCode);
  const loyaltyBlockedByCoupon = couponApplied && appliedPoints !== offerPoints;
  const loyaltySelected = offerPoints > 0 && appliedPoints === offerPoints && !loyaltyBlockedByCoupon;
  const loyaltyConfirmed = loyaltySelected && isLoyaltyOtpComplete(cart.loyalty?.otp);
  const loyaltyDiscount = loyaltyConfirmed ? getAppliedLoyaltyDiscountRupee({ ...cart, coupon: null }) : 0;
  const loyaltyOtpPending = loyaltySelected && !loyaltyConfirmed;
  const payable = Math.max(0, total - loyaltyDiscount);

  const loyaltyErrorMessage = (error) =>
    error instanceof V2ApiError ? error.message : error?.message || 'Could not send the loyalty OTP.';

  const turnLoyaltyOff = async () => {
    otpTouchedRef.current = false;
    otpDraftRef.current = '';
    setOtpDraft('');
    await setCartLoyalty(null);
    await loadCart();
  };

  const toggleLoyalty = async () => {
    if (!offerPoints || busy || loyaltyBlockedByCoupon || resendingOtp) return;
    setLoyaltyError('');
    if (loyaltySelected) {
      setBusy(true);
      try {
        await turnLoyaltyOff();
      } finally {
        setBusy(false);
      }
      return;
    }

    setCart((prev) => ({
      ...prev,
      loyalty: { points: offerPoints, otp: '', sentTo: '' },
    }));
    setBusy(true);
    try {
      await setCartLoyalty({ points: offerPoints });
      const result = await initiateLoyaltyRedemption({ phone: mobile, points: offerPoints });
      otpTouchedRef.current = false;
      otpDraftRef.current = '';
      setOtpDraft('');
      await setCartLoyalty({ points: offerPoints, sentTo: result.sentTo, otp: '' });
      await loadCart();
    } catch (error) {
      await turnLoyaltyOff();
      setLoyaltyError(loyaltyErrorMessage(error));
    } finally {
      setBusy(false);
    }
  };

  const handleOtpChange = (next) => {
    const digits = normalizeLoyaltyOtp(next);
    otpTouchedRef.current = true;
    otpDraftRef.current = digits;
    setOtpDraft(digits);
    setLoyaltyError('');
  };

  const confirmLoyaltyOtp = async () => {
    if (!loyaltySelected || busy || !isLoyaltyOtpComplete(otpDraft)) return;
    setLoyaltyError('');
    setBusy(true);
    try {
      await setCartLoyalty({ points: offerPoints, sentTo: cart.loyalty?.sentTo || '', otp: otpDraft });
      otpTouchedRef.current = false;
      await loadCart();
    } finally {
      setBusy(false);
    }
  };

  const resendLoyaltyOtp = async () => {
    if (!offerPoints || !loyaltySelected || resendingOtp || busy) return;
    setLoyaltyError('');
    setResendingOtp(true);
    otpTouchedRef.current = false;
    otpDraftRef.current = '';
    setOtpDraft('');
    try {
      await setCartLoyaltyOtp('');
      const result = await initiateLoyaltyRedemption({ phone: mobile, points: offerPoints });
      await setCartLoyalty({ points: offerPoints, sentTo: result.sentTo, otp: '' });
      await loadCart();
    } catch (error) {
      await turnLoyaltyOff();
      setLoyaltyError(loyaltyErrorMessage(error));
    } finally {
      setResendingOtp(false);
    }
  };

  if (loading) {
    return (
      <PageContainer sx={{ px: { xs: 1.5, sm: 2 } }}>
        <CartPageHeader clearDisabled />
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={32} sx={{ color: v2Colors.primary }} />
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer sx={{ px: { xs: 1.5, sm: 2 }, pb: 4 }}>
      <CartPageHeader onClearAll={handleClearAll} clearDisabled={!hasItems || busy} />

      {!hasItems ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            py: 6,
            px: 2,
          }}
        >
          <ShoppingBagOutlined sx={{ fontSize: 56, color: v2Colors.textMuted, mb: 2 }} />
          <Typography variant="h6" fontWeight={700} sx={{ color: v2Colors.heading, mb: 1 }}>
            Your bag is empty
          </Typography>
          <Typography variant="body2" sx={{ color: v2Colors.textMuted, mb: 3, maxWidth: 280 }}>
            Add items from the catalog to see them here.
          </Typography>
          <Button
            onClick={() => navigate(paths.allProducts)}
            sx={{ borderRadius: 2, bgcolor: v2Colors.primary, '&:hover': { bgcolor: v2Colors.primaryDark } }}
          >
            Browse catalog
          </Button>
        </Box>
      ) : (
        <>
          {cart.products.map((line, index) => (
            <CartLineRow
              key={`${getCartLineKey(line)}-${index}`}
              line={line}
              onDecrease={handleDecrease}
              onIncrease={handleIncrease}
              onOpenProduct={goToProduct}
              busy={busy}
            />
          ))}

          <FreeDeliveryPromotion totalAmount={total} />
          <CartSuggestions cart={cart} phone={mobile} onLoyalty={handleLoyaltyOffer} />

          <TextField
            placeholder="Any special instructions? (e.g. ring bell twice)"
            fullWidth
            value={instructionInput}
            inputProps={{ maxLength: 100 }}
            onChange={(e) => setInstructionInput(e.target.value)}
            onBlur={() => {
              setCartInstructions(instructionInput).catch(() => {});
            }}
            disabled={busy}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                bgcolor: v2Colors.white,
                borderRadius: 2,
                '& fieldset': { borderColor: v2Colors.borderMuted },
                '&:hover fieldset': { borderColor: v2Colors.border },
                '&.Mui-focused fieldset': { borderColor: v2Colors.primary },
              },
            }}
          />

          <Box sx={cardSx}>
            <SummaryRow label="Subtotal" value={`₹${itemSubtotal}`} />
            {customizationTotal > 0 ? (
              <SummaryRow label="Customization" value={`₹${customizationTotal}`} />
            ) : null}
            {offerPoints > 0 ? (
              <NaatuPointsRow
                points={offerPoints}
                selected={loyaltySelected}
                confirmed={loyaltyConfirmed}
                discount={loyaltyDiscount}
                disabled={busy || loyaltyBlockedByCoupon}
                blockedByCoupon={loyaltyBlockedByCoupon}
                onToggle={toggleLoyalty}
                sentTo={cart.loyalty?.sentTo || ''}
                otp={otpDraft}
                onOtpChange={handleOtpChange}
                onConfirm={confirmLoyaltyOtp}
                onResend={resendLoyaltyOtp}
                resending={resendingOtp}
                error={loyaltyError}
              />
            ) : null}
            {loyaltyDiscount > 0 ? (
              <SummaryRow label="Naatu points" value={`−₹${loyaltyDiscount}`} />
            ) : null}
            <Divider sx={{ my: 1.5, borderColor: v2Colors.border }} />
            <SummaryRow label="Total" value={`₹${payable}`} bold />
          </Box>

          {loyaltyOtpPending ? (
            <Typography
              variant="caption"
              sx={{ color: v2Colors.primaryMuted, display: 'block', textAlign: 'center', mb: 1 }}
            >
              Confirm the OTP to use your points, or turn points off to continue.
            </Typography>
          ) : null}

          <Button
            fullWidth
            size="large"
            disabled={busy || resendingOtp || loyaltyOtpPending}
            onClick={async () => {
              try {
                await setCartInstructions(instructionInput);
              } catch {
                // checkout reads cache when possible
              }
              trackV2InitiateCheckout({
                cart,
                session: { mobile, user },
              });
              navigate(paths.checkout);
            }}
            sx={{
              borderRadius: 2,
              py: 1.5,
              fontWeight: 700,
              fontSize: '1rem',
              bgcolor: v2Colors.primary,
              '&:hover': { bgcolor: v2Colors.primaryDark },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
              }}
            >
              <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ShoppingBagOutlined sx={{ fontSize: 20 }} />
                Proceed to checkout
              </Box>
              <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                ₹{payable}
                <ArrowForwardRounded sx={{ fontSize: 20 }} />
              </Box>
            </Box>
          </Button>
        </>
      )}

      {customizeProduct ? (
        <ProductCustomizationSheet
          open={sheetOpen}
          onClose={closeCustomizeSheet}
          customizations={Object.entries(customizeProduct.customizations ?? {})}
          selectedCustomizations={customization.selected}
          onSelectSingle={customization.selectSingle}
          onToggleCheckbox={customization.toggleCheckbox}
          lineTotal={computeLineUnitPrice(customizeLine?.variant, customization.resolved)}
          adding={addingCustom}
          onConfirm={handleConfirmCustomize}
        />
      ) : null}
    </PageContainer>
  );
}
